/**
 * As caixas de plano — a ficha técnica de cada componente impresso à parte.
 *
 * Uma OS de livreto tem dois planos: a capa numa máquina, o miolo em outra. As
 * duas caixas ficam lado a lado, e cada uma tem duas colunas internas de
 * rótulos com o valor logo abaixo, a 7–9 pt.
 *
 * A leitura é por **rótulo conhecido**: acha o rótulo, pega o item que estiver
 * abaixo dele na mesma coluna. Rótulo sem valor (o "Solda" do vinil) fica sem
 * valor e pronto; valor sem rótulo vira aviso, nunca some.
 */

import type { PlanoLido } from '@pcp/dominio';
import type { ItemDeTexto } from './pdf.js';

/** Rótulos que a OS imprime dentro da caixa de plano (ANATOMIA §3.3). */
const ROTULOS = [
  'Form.final(cm)',
  'Form.final(mm)',
  'Mat.orçado',
  'Montagem',
  'Impressora',
  'Abertura',
  'Refile / Sangr.',
  'Form.impressão',
  'Outros detalhes',
  'Outros',
  'Solda',
] as const;

/** Rótulo que existe sozinho, sem valor embaixo. */
const SEM_VALOR = new Set(['Solda', 'Outros']);

const RE_PLANO = /^Plano\s*#(\d+)$/;
const RE_TIPO = /^(Cores|Págs-Cores\/Lâms)$/;
/** "134-1x1 / 67" e "86-4x4 / 22(21,5)". */
const RE_PAGS = /^(\d+)-(\S+)\s*\/\s*(.+)$/;
/** "26x36 = 2" e "3x3cm" (vinil, sem contagem). */
const RE_FORMATO = /^(.+?)\s*=\s*(\d+)$/;
/** "OF90(66x96)" */
const RE_MATERIAL = /^([^(]+)\(([^)]*)\)$/;

/** Distância máxima entre um rótulo e o valor logo abaixo dele. */
const ALCANCE_VALOR = 13;

export function lerPlanos(itens: readonly ItemDeTexto[], avisos: string[]): PlanoLido[] {
  const cabecalhos = itens
    .filter((i) => RE_PLANO.test(i.texto))
    .sort((a, b) => a.x - b.x);
  if (cabecalhos.length === 0) return [];

  const fimDosPlanos = acharFimDosPlanos(itens, cabecalhos[0]!.y);

  return cabecalhos.map((cab, indice) => {
    const proximo = cabecalhos[indice + 1];
    const xInicio = cab.x - 2;
    const xFim = proximo ? proximo.x - 2 : Number.POSITIVE_INFINITY;

    const daCaixa = itens.filter(
      (i) => i.x >= xInicio && i.x < xFim && i.y >= cab.y - 2 && i.y < fimDosPlanos,
    );

    return montarPlano(cab, daCaixa, avisos);
  });
}

/** Os planos vão até a primeira tabela de etapas. */
function acharFimDosPlanos(itens: readonly ItemDeTexto[], yDoPrimeiroPlano: number): number {
  const etapas = itens
    .filter((i) => /^Etapas\s/.test(i.texto) && i.y > yDoPrimeiroPlano)
    .sort((a, b) => a.y - b.y)[0];
  return etapas ? etapas.y - 2 : Number.POSITIVE_INFINITY;
}

function montarPlano(
  cabecalho: ItemDeTexto,
  itens: readonly ItemDeTexto[],
  avisos: string[],
): PlanoLido {
  const numero = Number(RE_PLANO.exec(cabecalho.texto)?.[1] ?? '1');

  // O tipo fica à direita do "Plano #n", na mesma linha.
  const tipoItem = itens.find(
    (i) => Math.abs(i.y - cabecalho.y) <= 2 && i.x > cabecalho.x && RE_TIPO.test(i.texto),
  );
  const tipo: PlanoLido['tipo'] =
    tipoItem?.texto === 'Págs-Cores/Lâms' ? 'pags_cores_lams' : 'cores';

  const usados = new Set<ItemDeTexto>();
  if (tipoItem) usados.add(tipoItem);
  usados.add(cabecalho);

  const valorDe = (rotulo: ItemDeTexto): string | null => {
    const abaixo = itens
      .filter(
        (i) =>
          !usados.has(i) &&
          Math.abs(i.x - rotulo.x) <= 3 &&
          i.y > rotulo.y + 2 &&
          i.y <= rotulo.y + ALCANCE_VALOR,
      )
      .sort((a, b) => a.y - b.y)[0];
    if (!abaixo) return null;
    usados.add(abaixo);
    return abaixo.texto;
  };

  const campos = new Map<string, string | null>();

  // O valor do tipo (cores ou páginas) fica abaixo do próprio rótulo do tipo.
  let valorTipo: string | null = null;
  if (tipoItem) {
    const abaixo = itens
      .filter(
        (i) =>
          !usados.has(i) &&
          Math.abs(i.x - tipoItem.x) <= 3 &&
          i.y > tipoItem.y + 2 &&
          i.y <= tipoItem.y + ALCANCE_VALOR,
      )
      .sort((a, b) => a.y - b.y)[0];
    if (abaixo) {
      valorTipo = abaixo.texto;
      usados.add(abaixo);
    }
  }

  for (const item of [...itens].sort((a, b) => a.y - b.y || a.x - b.x)) {
    if (usados.has(item)) continue;
    if (!ROTULOS.includes(item.texto as (typeof ROTULOS)[number])) continue;
    usados.add(item);
    campos.set(item.texto, SEM_VALOR.has(item.texto) ? null : valorDe(item));
  }

  for (const item of itens) {
    if (!usados.has(item)) {
      avisos.push(`Plano #${numero}: valor sem rótulo — "${item.texto}"`);
    }
  }

  // ---- normalização ----

  let cores: string | null = null;
  let paginas: number | null = null;
  let laminas: string | null = null;
  if (tipo === 'pags_cores_lams' && valorTipo) {
    const m = RE_PAGS.exec(valorTipo);
    if (m) {
      paginas = Number(m[1]);
      cores = m[2] ?? null;
      laminas = m[3] ?? null;
    } else {
      cores = valorTipo;
      avisos.push(`Plano #${numero}: páginas/cores/lâminas em formato novo — "${valorTipo}"`);
    }
  } else {
    cores = valorTipo;
  }

  const formatoBruto = campos.get('Form.final(cm)') ?? campos.get('Form.final(mm)') ?? null;
  const formatoFinalUnidade: PlanoLido['formatoFinalUnidade'] = campos.has('Form.final(cm)')
    ? 'cm'
    : campos.has('Form.final(mm)')
      ? 'mm'
      : null;
  let formatoFinal: string | null = formatoBruto;
  let pecasPorFolha: number | null = null;
  if (formatoBruto) {
    const m = RE_FORMATO.exec(formatoBruto);
    if (m) {
      formatoFinal = (m[1] ?? '').trim();
      pecasPorFolha = Number(m[2]);
    }
  }

  const materialBruto = campos.get('Mat.orçado') ?? null;
  let materialCodigo: string | null = materialBruto;
  let materialFormato: string | null = null;
  if (materialBruto) {
    const m = RE_MATERIAL.exec(materialBruto);
    if (m) {
      materialCodigo = (m[1] ?? '').trim();
      materialFormato = m[2] ?? null;
    }
  }

  const montagemBruta = campos.get('Montagem') ?? null;
  const tiraRetira = !!montagemBruta && /\bTR\b/.test(montagemBruta);
  const montagem = montagemBruta ? montagemBruta.replace(/\s*TR\s*$/, '').trim() : null;

  const outros = campos.get('Outros detalhes') ?? campos.get('Outros') ?? null;
  const faca = outros ? (/Faca:\s*(\S+)/.exec(outros)?.[1] ?? null) : null;
  const lombada = outros ? (/Lombada:\s*(\S+)/.exec(outros)?.[1] ?? null) : null;

  return {
    numero,
    tipo,
    cores,
    paginas,
    laminas,
    formatoFinal,
    formatoFinalUnidade,
    pecasPorFolha,
    materialCodigo,
    materialFormato,
    montagem,
    tiraRetira,
    impressora: campos.get('Impressora') ?? null,
    abertura: campos.get('Abertura') ?? null,
    refileSangria: campos.get('Refile / Sangr.') ?? null,
    formatoImpressao: campos.get('Form.impressão') ?? null,
    faca,
    lombada,
    solda: campos.has('Solda'),
    outrosDetalhes: outros,
  };
}
